import { apiClient } from "@/lib/api/client";
import type { components } from "@/lib/api/types";
import { useAuthStore } from "@/stores/auth-store";

type AuthSuccessResponse = components["schemas"]["AuthSuccessResponse"];

export interface GoogleSSOError {
	message: string;
	code?: string;
}

/**
 * Initiate Google OAuth login flow.
 * Redirects user to Google authorization page.
 */
export async function initiateGoogleLogin(): Promise<void> {
	try {
		const { data, error, response } = await apiClient.POST("/api/v1/auth/google/login");

		if (error || !data) {
			throw new Error(
				response.status === 503
					? "Google OAuth is not configured. Please contact support."
					: "Failed to initiate Google login"
			);
		}

		// Redirect to Google authorization page
		window.location.href = data.authorization_url;
	} catch (error) {
		console.error("Failed to initiate Google login:", error);
		throw error;
	}
}

/**
 * Handle Google OAuth callback.
 * Exchanges authorization code for user tokens.
 */
export async function handleGoogleCallback(): Promise<
	{ success: true; data: AuthSuccessResponse } | { success: false; error: GoogleSSOError }
> {
	try {
		// Extract code and state from URL
		const params = new URLSearchParams(window.location.search);
		const code = params.get("code");
		const state = params.get("state");

		if (!code || !state) {
			return {
				success: false,
				error: {
					message: "Missing OAuth parameters",
					code: "missing_params",
				},
			};
		}

		// Exchange code for tokens
		const { data, error, response } = await apiClient.GET("/api/v1/auth/google/callback", {
			params: {
				query: { code, state },
			},
		});

		if (error || !data) {
			return {
				success: false,
				error: {
					message:
						response.status === 401
							? "Google authentication failed or expired. Please try again."
							: response.status === 409
								? "This email is already registered but not verified. Please verify your email first."
								: "Google sign-in failed. Please try again.",
					code: response.status.toString(),
				},
			};
		}

		// Update auth store (same as email/password login)
		useAuthStore.getState().setAuth({
			user: data.user,
			accessToken: data.api_key,
			refreshToken: data.refresh_token,
			accessExpiresAt: data.access_expires_at || "",
			refreshExpiresAt: data.refresh_expires_at || "",
		});

		return { success: true, data };
	} catch (error) {
		console.error("Google callback error:", error);
		return {
			success: false,
			error: {
				message: "An unexpected error occurred during Google sign-in.",
			},
		};
	}
}
